import { BEIGE_300, BEIGE_600, BOX_SHADOW_XS, GRAY_900, WHITE } from 'constants/styling/theme'
import { FC, Fragment, useCallback, useMemo, useState } from 'react'
import { Stack, Typography } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { applyFeeDiscount, formatPrice } from 'utils/price'
import { getDateString, getUserTimezone } from 'utils/time'
import { useGalleryDeal, useGalleryVirtualVisit } from '../../contexts'

import { MUIButton } from 'components/common/MUIButton'
import { Nullable } from 'models/helpers'
import { ProductType } from 'constants/product'
import { UpsellPayment } from '../../../common'
import { ZERO_DECIMAL } from 'constants/misc'
import { identifiableProduct } from 'utils/serialization/productIdentifier'
import { logger } from 'utils/logging'
import { mutationToAPIRequest } from 'utils/reactQuery/queryHelpers'
import { useAuth0 } from 'utils/auth'
import { useCanUserOrder } from 'utils/user/canUserOrder'
import { useClientGallery } from '../_main/contexts'
import { useExtendVirtualVisitHosting } from 'dataQueries/visual.query'
import { useGetVirtualVisitExtensionPrice, useUserMe } from 'dataQueries'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * Client gallery virtual visit info card component
 * 
 * @example
 * <ClientGalleryVirtualVisitInfoCard />
 */

export const ClientGalleryVirtualVisitInfoCard: FC = () => {
  const { t } = useTranslation(['virtual_visit_info', 'gallery', 'gallery_staging_flow'])
  const { spawnErrorToast } = useSnackbar()
  const { isAuthenticated } = useAuth0()

  const canUserOrder = useCanUserOrder()

  const { roles } = useAuth0()

  const { assignmentId } = useClientGallery()
  const { dealData } = useGalleryDeal()
  const { userPropertyWorkspaceId } = useUserData()
  const {
    virtualVisitExpirationDate,
    virtualVisitExpired
  } = useGalleryVirtualVisit()

  const { data: user } = useUserMe(isAuthenticated)

  const virtualExtensionPrice = useGetVirtualVisitExtensionPrice(assignmentId, userPropertyWorkspaceId, roles)
  const virtualExtensionPriceData = useMemo(() => virtualExtensionPrice.data?.data, [virtualExtensionPrice])

  const extendVirtualVisitMutation = useExtendVirtualVisitHosting()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const extendVirtualVisitRequest = useMemo(() => mutationToAPIRequest(extendVirtualVisitMutation), [extendVirtualVisitMutation.status, extendVirtualVisitMutation.error])

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  const extensionPrice = useMemo(() => virtualExtensionPriceData?.fee, [virtualExtensionPriceData])
  const extensionProductId = useMemo(() => virtualExtensionPriceData?.productId, [virtualExtensionPriceData])
  const currency = useMemo(() => extensionPrice?.currency, [extensionPrice])
  const extensionTotal = useMemo(() => !!extensionPrice ? applyFeeDiscount({ ...extensionPrice, discountPercentage: dealData?.financials.discount ?? ZERO_DECIMAL }) : undefined, [extensionPrice, dealData?.financials.discount])
  const expirationDateString = useMemo(() => !!virtualVisitExpirationDate ? getDateString(virtualVisitExpirationDate, { timezone: getUserTimezone(user) }) : '', [virtualVisitExpirationDate, user])

  const infoTitle = useMemo(() => {
    if (virtualVisitExpired) return t('reactivate_info_title')
    return t('extend_info_title', { expirationDate: expirationDateString })
  }, [expirationDateString, t, virtualVisitExpired])

  const infoBody = useMemo(() => {
    if (virtualVisitExpired) return 'reactivate_info_body'
    return 'extend_info_body'
  }, [virtualVisitExpired])

  /**
   * Extends the hosting, identifying the picked payment option by its Product2 code — the only form
   * the extension wire accepts. The payment intent already exists here, so an option without a code
   * aborts loudly rather than sending a request the API would reject.
   */
  const handleExtendHosting = useCallback((paymentIntentId: string | undefined, billingProductCode: Nullable<string>) => {
    const billingProduct = identifiableProduct(billingProductCode)

    if (!billingProduct) {
      spawnErrorToast(t('gallery_staging_flow:upsell_abort_error'))
      logger.error('Virtual visit extension not submitted - the billing product carries no product code', undefined, {
        context: { component: 'ClientGalleryVirtualVisitInfoCard', action: 'handleExtendHosting', metadata: { assignmentId } },
      })

      return
    }

    extendVirtualVisitMutation.mutate({ assignmentId, workspaceId: userPropertyWorkspaceId, billingProduct, paymentIntentId })
  }, [assignmentId, extendVirtualVisitMutation, spawnErrorToast, t, userPropertyWorkspaceId])

  return (
    <Fragment>

      <Stack
        width="100%"
        borderRadius=".8rem"
        boxShadow={BOX_SHADOW_XS}
        padding="1.6rem"
        boxSizing="border-box"
        gap="1.6rem"
        sx={{ backgroundColor: WHITE }}
      >
        <Stack
          borderRadius=".8rem"
          padding="1.2rem"
          color={GRAY_900}
          sx={{
            border: `.1rem solid ${BEIGE_600}`,
            backgroundColor: BEIGE_300
          }}
        >
          <Typography variant="text-sm" fontWeight="semiBold">{infoTitle}</Typography>
          <Typography variant="text-sm" fontWeight="regular">
            <Trans t={t} i18nKey={infoBody} values={{ price: formatPrice(extensionPrice, currency) }}>
              <span></span>
            </Trans>
          </Typography>
        </Stack>
        {canUserOrder && (
          <Stack flexDirection="row" justifyContent="flex-end">
            <MUIButton
              onClick={() => setIsModalOpen(true)}
            >
              {t(virtualVisitExpired ? 'reactivate_cta' : 'extend_cta')}
            </MUIButton>
          </Stack>
        )}
      </Stack>

      <UpsellPayment
        assignmentId={assignmentId}
        productId={extensionProductId}
        productQuantity={1}
        total={extensionTotal}
        isOpen={isModalOpen}
        beforeConfirmAction={handleExtendHosting}
        beforeConfirmActionState={extendVirtualVisitRequest?.state}
        beforeConfirmActionMutation={extendVirtualVisitMutation}
        onClose={() => setIsModalOpen(false)}
        afterClosed={() => extendVirtualVisitMutation.reset()}
        successTitle={t(virtualVisitExpired ? 'reactivate_success' : 'extend_success')}
        productType={ProductType.VIRTUAL_VISIT}
      >
        <Trans t={t} i18nKey={virtualVisitExpired ? 'reactivate_confirm_text' : 'extend_confirm_text'} values={{ price: formatPrice(extensionTotal, currency) }}>
          <span></span>
        </Trans>
      </UpsellPayment>

    </Fragment>
  )
}
